import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Siren, ShieldCheck, Check, Sparkles, X } from "lucide-react";
import { api } from "../lib/api";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";

/**
 * Pain SOS — fixed top-right button. Two-step flare protocol:
 *   1. Log what triggered the spasm (autosaves on submit)
 *   2. Reminder to put the Back Pain Transformer on ASAP
 * Saved to /api/flare-logs and surfaced in Settings → Flare reflections.
 */
export default function PainSOSButton() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1); // 1 = trigger, 2 = transformer, 3 = saved
  const [trigger, setTrigger] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  function reset() {
    setStep(1);
    setTrigger("");
    setErr("");
    setSaving(false);
  }

  function handleOpen(next) {
    setOpen(next);
    if (!next) {
      // small delay to avoid flicker as dialog closes
      setTimeout(reset, 250);
    }
  }

  async function saveTrigger() {
    const text = trigger.trim();
    if (!text) {
      setErr("Tell yourself what may have triggered this. Even one word counts.");
      return;
    }
    setErr("");
    setSaving(true);
    try {
      await api.post("/flare-logs", { trigger_text: text });
      setStep(2);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save right now. Try again.");
    } finally {
      setSaving(false);
    }
  }

  function finish() {
    setStep(3);
    setTimeout(() => handleOpen(false), 1400);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogTrigger asChild>
        <motion.button
          type="button"
          aria-label="Pain SOS"
          data-testid="pain-sos-button"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.4, type: "spring", stiffness: 220, damping: 18 }}
          whileTap={{ scale: 0.92 }}
          className="fixed bottom-24 right-4 z-40 w-12 h-12 rounded-full flex items-center justify-center shadow-[0_0_24px_rgba(239,68,68,0.55)] border border-[#F87171]/50 bg-gradient-to-br from-[#F87171] to-[#B91C1C] text-white active:scale-95 transition-transform"
        >
          <span className="absolute inset-0 rounded-full bg-[#F87171]/30 animate-ping" style={{ animationDuration: "2.4s" }} />
          <Siren size={20} strokeWidth={2.2} className="relative" />
        </motion.button>
      </DialogTrigger>

      <DialogContent
        className="border border-[#F87171]/30 bg-[#0A1530]/95 backdrop-blur-xl rounded-3xl shadow-[0_24px_60px_rgba(0,0,0,0.6),0_0_0_1px_rgba(248,113,113,0.08)] max-w-md p-0"
        data-testid="pain-sos-dialog"
      >
        <div className="p-6 space-y-5">
          {/* Header */}
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-[#F87171]/15 border border-[#F87171]/30 flex items-center justify-center shrink-0">
              {step === 3 ? (
                <Check size={22} className="text-[#22D3EE]" strokeWidth={2.2} />
              ) : (
                <Siren size={22} className="text-[#F87171]" strokeWidth={1.8} />
              )}
            </div>
            <div className="flex-1">
              <div className="bps-eyebrow text-[#F87171]">Pain SOS</div>
              <DialogTitle className="font-display text-xl font-medium tracking-tight text-white leading-snug">
                {step === 1 && "Pause. Breathe. Notice."}
                {step === 2 && "Put on the Transformer."}
                {step === 3 && "Logged. You're not alone in this."}
              </DialogTitle>
            </div>
          </div>

          {/* Step indicator */}
          {step < 3 && (
            <div className="flex items-center gap-1.5" data-testid="sos-progress">
              {[1, 2].map((n) => (
                <div
                  key={n}
                  className="h-1 flex-1 rounded-full transition-all"
                  style={{ background: n <= step ? "#22D3EE" : "rgba(255,255,255,0.10)" }}
                />
              ))}
            </div>
          )}

          {/* STEP 1: Log the trigger */}
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div
                key="step-1"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25 }}
                className="space-y-4"
                data-testid="sos-step-1"
              >
                <p className="text-sm text-white/85 leading-relaxed">
                  What were you doing or thinking that may have triggered the spasm?
                </p>
                <p className="text-xs text-[#9AAEC6] leading-relaxed">
                  No wrong answers. Even a word or fragment helps your nervous system feel seen.
                </p>
                <textarea
                  value={trigger}
                  onChange={(e) => setTrigger(e.target.value)}
                  rows={4}
                  placeholder="Lifted a grocery bag the wrong way… argued with mom… sat too long…"
                  data-testid="sos-trigger-input"
                  autoFocus
                  className="w-full rounded-2xl bg-[#03101F]/70 border border-white/10 focus:border-[#22D3EE]/50 focus:outline-none focus:ring-2 focus:ring-[#22D3EE]/15 px-4 py-3 text-sm text-white placeholder:text-[#5A6E87]/70 leading-relaxed resize-none transition-all"
                />
                {err && <div className="text-xs text-[#F87171]" data-testid="sos-error">{err}</div>}
                <button
                  type="button"
                  onClick={saveTrigger}
                  disabled={saving}
                  data-testid="sos-step-1-continue"
                  className="w-full py-3.5 rounded-full bg-[#22D3EE] text-[#03101F] font-semibold disabled:opacity-30 hover:-translate-y-0.5 transition-all shadow-[0_0_18px_rgba(34,211,238,0.35)]"
                >
                  {saving ? "Saving…" : "Continue"}
                </button>
              </motion.div>
            )}

            {/* STEP 2: Transformer reminder */}
            {step === 2 && (
              <motion.div
                key="step-2"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25 }}
                className="space-y-4"
                data-testid="sos-step-2"
              >
                <div className="rounded-2xl border border-[#22D3EE]/30 bg-[#22D3EE]/[0.06] p-5 flex items-start gap-3">
                  <ShieldCheck size={18} className="text-[#22D3EE] mt-0.5 shrink-0" />
                  <div>
                    <div className="font-display text-base text-white leading-snug">
                      Put on The Back Pain Transformer™ now.
                    </div>
                    <p className="text-xs text-[#9AAEC6] mt-1.5 leading-relaxed">
                      Your harness gives your spine the external scaffolding it needs while
                      the spasm settles. Wear it for at least 30 minutes.
                    </p>
                  </div>
                </div>

                <ol className="space-y-2.5 text-sm text-white/85">
                  {[
                    "Stand or sit tall — don't curl.",
                    "Take 4 slow nasal breaths (in 4 · hold 4 · out 8).",
                    "Sip water. Soften the jaw and shoulders.",
                    "Wait. The wave will pass.",
                  ].map((line, i) => (
                    <li key={line} className="flex gap-3" data-testid={`sos-instruction-${i + 1}`}>
                      <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full border border-[#22D3EE]/55 text-[#22D3EE] font-mono text-[10px] font-semibold shrink-0">
                        {i + 1}
                      </span>
                      <span className="leading-relaxed">{line}</span>
                    </li>
                  ))}
                </ol>

                <button
                  type="button"
                  onClick={finish}
                  data-testid="sos-step-2-done"
                  className="w-full py-3.5 rounded-full bg-[#22D3EE] text-[#03101F] font-semibold hover:-translate-y-0.5 transition-all shadow-[0_0_18px_rgba(34,211,238,0.35)]"
                >
                  I've got my Transformer on
                </button>
              </motion.div>
            )}

            {/* STEP 3: Saved confirmation */}
            {step === 3 && (
              <motion.div
                key="step-3"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25 }}
                className="text-center py-4 space-y-2"
                data-testid="sos-step-3"
              >
                <Sparkles size={22} className="text-[#22D3EE] mx-auto" />
                <p className="text-sm text-white/85 leading-relaxed">
                  Your reflection is saved. Find every flare entry under
                  <span className="text-[#22D3EE]"> Settings → Flare reflections</span>.
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Close shortcut for step 1 */}
          {step === 1 && (
            <button
              type="button"
              onClick={() => handleOpen(false)}
              data-testid="sos-cancel"
              className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-mono uppercase tracking-[0.18em] text-[#7C8FA8] hover:text-white transition-colors py-1"
            >
              <X size={12} /> Not now
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
